/**
 * ft-hero-smart-money
 *
 * A hero section component featuring "Every 13F Filing. Every Position." messaging.
 * Includes animated badge, headline, AI chat input, and social proof.
 *
 * Precondition: None
 * Postcondition: Renders a fully styled hero section with AI chat functionality
 *
 * Attributes:
 * - placeholder: Chat input placeholder text
 *
 * Events:
 * - Redirects to /chat on submission
 *
 * Public API:
 * - focus(): Focus the chat input
 */

class HeroSmartMoney extends HTMLElement {
  private chatInput: HTMLElement | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return ["placeholder"];
  }

  connectedCallback(): void {
    this.render();
    this.setupEventListeners();
  }

  disconnectedCallback(): void {
    // Cleanup handled by garbage collection when shadow DOM is removed
  }

  attributeChangedCallback(): void {
    // Re-render on attribute changes
  }

  get placeholder(): string {
    return this.getAttribute("placeholder") || "Ask about institutional holdings...";
  }

  // Public API
  focus(): void {
    const input = this.chatInput?.shadowRoot?.querySelector("textarea");
    input?.focus();
  }

  private render(): void {
    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
      <style>${this.getStyles()}</style>
      ${this.getTemplate()}
    `;

    this.chatInput = this.shadowRoot.querySelector("ft-chat-input");
  }

  private getStyles(): string {
    return `
      :host {
        --hero-bg: var(--bg, #FAFAFB);
        --hero-text: var(--text, #1C1C1E);
        --hero-muted: var(--muted, #555555);
        --hero-primary: var(--primary, #E0524D);
        --hero-primary-pressed: var(--primary-pressed, #D53A2A);
        --hero-primary-edge: var(--primary-edge, #B33D39);
        --hero-input-bg: var(--input-bg, #ffffff);
        --hero-input-border: var(--input-border, #E2E4E8);
        --hero-radius: var(--radius, 8px);

        --ease-out-smooth: cubic-bezier(0.3, 0.7, 0.4, 1);
        --ease-out-spring: cubic-bezier(0.3, 0.7, 0.4, 1.5);
        --duration-fast: 150ms;
        --duration-normal: 250ms;
        --duration-slow: 400ms;
        --duration-enter: 600ms;

        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        min-height: 100%;
        font-family: Inter, system-ui, sans-serif;
        -webkit-font-smoothing: antialiased;
      }

      @media (prefers-color-scheme: dark) {
        :host {
          --hero-bg: var(--bg, #0b0b0c);
          --hero-text: var(--text, #ffffff);
          --hero-muted: var(--muted, #a3a3a3);
          --hero-input-bg: var(--input-bg, #14161a);
          --hero-input-border: var(--input-border, #272a31);
        }
      }

      .hero {
        padding: 60px 20px;
        text-align: center;
      }

      /* Badge */
      .badge {
        display: inline-block;
        padding: 6px 12px;
        font: 600 12px/1 Inter, sans-serif;
        color: var(--hero-primary);
        background: color-mix(in srgb, var(--hero-primary) 10%, transparent);
        border-radius: 20px;
        margin-bottom: 16px;
        will-change: transform, opacity;
      }

      @media (prefers-reduced-motion: no-preference) {
        .badge {
          opacity: 0;
          transform: translateY(10px) scale(0.95);
          animation: badgeEnter var(--duration-slow) var(--ease-out-spring) forwards;
          transition:
            transform var(--duration-normal) var(--ease-out-smooth),
            background var(--duration-normal) ease;
        }

        .badge:hover {
          transform: scale(1.05);
          background: color-mix(in srgb, var(--hero-primary) 18%, transparent);
        }
      }

      /* Headline */
      .headline {
        font: 900 clamp(32px, 6vw, 56px)/1.05 "Space Grotesk", sans-serif;
        letter-spacing: -0.02em;
        margin: 0 0 20px;
        text-wrap: balance;
        color: var(--hero-text);
        will-change: transform, opacity;
      }

      @media (prefers-reduced-motion: no-preference) {
        .headline {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeSlideUp var(--duration-enter) var(--ease-out-smooth) forwards;
          animation-delay: 150ms;
        }
      }

      /* Subheadline */
      .subheadline {
        font: 400 clamp(16px, 2vw, 18px)/1.6 Inter, sans-serif;
        color: var(--hero-muted);
        margin: 0 auto 32px;
        max-width: 560px;
        will-change: transform, opacity;
      }

      @media (prefers-reduced-motion: no-preference) {
        .subheadline {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeSlideUp var(--duration-enter) var(--ease-out-smooth) forwards;
          animation-delay: 250ms;
        }
      }

      /* Chat Input Wrapper */
      .chat-wrapper {
        max-width: 600px;
        margin: 0 auto var(--space-md);
        will-change: transform, opacity;
      }

      @media (prefers-reduced-motion: no-preference) {
        .chat-wrapper {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeSlideUp var(--duration-enter) var(--ease-out-smooth) forwards;
          animation-delay: 350ms;
        }
      }

      /* Social Proof */
      .social-proof {
        font: 500 14px/1.4 Inter, sans-serif;
        color: var(--hero-muted);
        will-change: transform, opacity;
      }

      @media (prefers-reduced-motion: no-preference) {
        .social-proof {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeSlideUp var(--duration-enter) var(--ease-out-smooth) forwards;
          animation-delay: 450ms;
        }
      }

      .highlight {
        color: var(--hero-text);
      }

      @media (prefers-reduced-motion: no-preference) {
        .highlight {
          transition: color var(--duration-normal) ease;
        }

        .social-proof:hover .highlight {
          color: var(--hero-primary);
        }
      }

      /* Animations */
      @keyframes fadeSlideUp {
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes badgeEnter {
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
    `;
  }

  private getTemplate(): string {
    return `
      <section class="hero">
        <span class="badge">AI-Powered</span>
        <h1 class="headline">Every 13F Filing. Every Position.</h1>
        <p class="subheadline">
          Stop digging through EDGAR. Start asking questions.
        </p>
        <div class="chat-wrapper">
          <ft-chat-input
            placeholder="${this.placeholder}"
            button-text="Ask AI"
            hide-disclaimer
          ></ft-chat-input>
          <ft-chat-suggestions suggestions='[{"text":"What is Berkshire buying?","url":"/chat?q=What+is+Berkshire+buying%3F&cik=1067983"},{"text":"Show me Bridgewater\u0027s top holdings","url":"/chat?q=Show+me+top+holdings&cik=1350694"}]'></ft-chat-suggestions>
        </div>
        <p class="social-proof">
          <span class="highlight">Free tier available</span> · No credit card
        </p>
      </section>
    `;
  }

  private setupEventListeners(): void {
    // Listen for chat submissions and redirect to /chat
    this.shadowRoot?.addEventListener("chat-submit", (e: Event) => {
      const customEvent = e as CustomEvent<{ text: string }>;
      const query = customEvent.detail.text;
      if (query) {
        window.location.href = `/chat?q=${encodeURIComponent(query)}`;
      }
    });
  }
}

// Guard against duplicate registration

customElements.define("ft-hero-smart-money", HeroSmartMoney);
